import api from '~/store/services/api';
import urlConstants from '~/lib/constants/url.constants';
import {
  DiscussionOfWeightAttribute,
  FormAttribute,
  LevelAttribute,
  RefeedingRiskAttribute,
  SaferStageAttribute,
  ModalityAttribute,
  PhaseStageAttribute,
  MedicalStabilityAttribute,
  PsychiatricStabilityAttribute,
  CreateAttributesParams,
  CreateAttributesResponse,
  GetTreatmentAttributesResponse,
  GetTreatmentAttributesRequest,
} from '~/lib/types/treatmentAttributes/treatmentAttributes';
import { CacheTag } from './CacheTag';
import { HttpRequestMethod } from '~/store';
import { ENUMERATION_CACHE_TIME } from './enumerations';

export const treatmentAttributesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getDiscussionOfWeightAttributes: build.query<
      DiscussionOfWeightAttribute[],
      string
    >({
      providesTags: (_, __, treatmentEpisodeId) => [
        { id: treatmentEpisodeId, type: CacheTag.DiscussionOfWeightAttributes },
      ],
      query: (treatmentEpisodeId) => ({
        url: urlConstants.treatmentAttributes.getEpisodeDiscussionOfWeights(
          treatmentEpisodeId,
        ),
      }),
    }),
    getFormTreatmentAttributes: build.query<FormAttribute[], string>({
      providesTags: (_, __, treatmentEpisodeId) => [
        { id: treatmentEpisodeId, type: CacheTag.FormAttributes },
      ],
      query: (treatmentEpisodeId) => ({
        url: urlConstants.treatmentAttributes.getEpisodeForm(
          treatmentEpisodeId,
        ),
      }),
    }),
    getLevelTreatmentAttributes: build.query<LevelAttribute[], string>({
      providesTags: (_, __, treatmentEpisodeId) => [
        { id: treatmentEpisodeId, type: CacheTag.LevelAttributes },
      ],
      query: (treatmentEpisodeId) => ({
        url: urlConstants.treatmentAttributes.getEpisodeLevels(
          treatmentEpisodeId,
        ),
      }),
    }),
    getMedicalStabilityTreatmentAttributes: build.query<
      MedicalStabilityAttribute[],
      string
    >({
      providesTags: (_, __, treatmentEpisodeId) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.MedicalStabilityAttributes,
        },
      ],
      query: (treatmentEpisodeId) => ({
        url: urlConstants.treatmentAttributes.getEpisodeMedicalStabilities(
          treatmentEpisodeId,
        ),
      }),
    }),
    getModalityTreatmentAttributes: build.query<ModalityAttribute[], string>({
      providesTags: (_, __, treatmentEpisodeId) => [
        { id: treatmentEpisodeId, type: CacheTag.ModalityAttributes },
      ],
      query: (treatmentEpisodeId) => ({
        url: urlConstants.treatmentAttributes.getEpisodeModality(
          treatmentEpisodeId,
        ),
      }),
    }),
    getPhaseStageTreatmentAttributes: build.query<
      PhaseStageAttribute[],
      string
    >({
      providesTags: (_, __, treatmentEpisodeId) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.PhaseStageAttributes,
        },
      ],
      query: (treatmentEpisodeId) => ({
        url: urlConstants.treatmentAttributes.getEpisodePhaseStages(
          treatmentEpisodeId,
        ),
      }),
    }),
    getPsychiatricStabilityTreatmentAttributes: build.query<
      PsychiatricStabilityAttribute[],
      string
    >({
      providesTags: (_, __, treatmentEpisodeId) => [
        {
          id: treatmentEpisodeId,
          type: CacheTag.PsychiatricStabilityAttributes,
        },
      ],
      query: (treatmentEpisodeId) => ({
        url: urlConstants.treatmentAttributes.getEpisodePsychiatricStabilities(
          treatmentEpisodeId,
        ),
      }),
    }),
    getRefeedingRiskAttributes: build.query<RefeedingRiskAttribute[], string>({
      providesTags: (_, __, treatmentEpisodeId) => [
        { id: treatmentEpisodeId, type: CacheTag.RefeedingRiskAttributes },
      ],
      query: (treatmentEpisodeId) => ({
        url: urlConstants.treatmentAttributes.getEpisodeRefeedingRisks(
          treatmentEpisodeId,
        ),
      }),
    }),
    getSaferStageAttributes: build.query<SaferStageAttribute[], string>({
      providesTags: (_, __, treatmentEpisodeId) => [
        { id: treatmentEpisodeId, type: CacheTag.SaferStageAttributes },
      ],
      query: (treatmentEpisodeId) => ({
        url: urlConstants.treatmentAttributes.getEpisodeSaferStages(
          treatmentEpisodeId,
        ),
      }),
    }),
    createAttributes: build.mutation<
      CreateAttributesResponse,
      CreateAttributesParams
    >({
      invalidatesTags: (_, __, args) => {
        const invalidateTags = [
          {
            id: args.treatmentEpisodeId,
            type: CacheTag.CarePlans,
          },
          {
            id: args.treatmentEpisodeId,
            type: CacheTag.DiscussionOfWeightAttributes,
          },
          {
            id: args.treatmentEpisodeId,
            type: CacheTag.FormAttributes,
          },
          {
            id: args.treatmentEpisodeId,
            type: CacheTag.LevelAttributes,
          },
          {
            id: args.treatmentEpisodeId,
            type: CacheTag.MedicalStabilityAttributes,
          },
          {
            id: args.treatmentEpisodeId,
            type: CacheTag.ModalityAttributes,
          },
          {
            id: args.treatmentEpisodeId,
            type: CacheTag.PhaseStageAttributes,
          },
          {
            id: args.treatmentEpisodeId,
            type: CacheTag.PsychiatricStabilityAttributes,
          },
          {
            id: args.treatmentEpisodeId,
            type: CacheTag.RefeedingRiskAttributes,
          },
          {
            id: args.treatmentEpisodeId,
            type: CacheTag.SaferStageAttributes,
          },
        ];
        if (args.attributes.noteId) {
          invalidateTags.push({
            id: `${args.treatmentEpisodeId}-${args.attributes.noteId}`,
            type: CacheTag.TreatmentEpisodeAttributes,
          });
        }
        return invalidateTags;
      },
      query: ({ treatmentEpisodeId, attributes }) => ({
        body: attributes,
        method: HttpRequestMethod.POST,
        url: urlConstants.treatmentAttributes.createAttributes(
          treatmentEpisodeId,
        ),
      }),
    }),
    getTreatmentEpisodeAttributes: build.query<
      GetTreatmentAttributesResponse,
      GetTreatmentAttributesRequest
    >({
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      providesTags: (_, __, { treatmentEpisodeId, noteId }) => [
        {
          id: `${treatmentEpisodeId}-${noteId}`,
          type: CacheTag.TreatmentEpisodeAttributes,
        },
      ],
      query: ({ treatmentEpisodeId, noteId }) => ({
        params: {
          noteId,
        },
        url: urlConstants.treatmentAttributes.getAttributes(treatmentEpisodeId),
      }),
    }),
  }),
});

export const {
  useGetDiscussionOfWeightAttributesQuery,
  useGetLevelTreatmentAttributesQuery,
  useGetMedicalStabilityTreatmentAttributesQuery,
  useGetModalityTreatmentAttributesQuery,
  useGetFormTreatmentAttributesQuery,
  useGetPhaseStageTreatmentAttributesQuery,
  useGetPsychiatricStabilityTreatmentAttributesQuery,
  useGetSaferStageAttributesQuery,
  useGetRefeedingRiskAttributesQuery,
  useCreateAttributesMutation,
  useGetTreatmentEpisodeAttributesQuery,
} = treatmentAttributesApi;
